import ImageList, { type ImageListProps } from '@mui/material/ImageList';
import { type ImageListItemProps } from '@mui/material/ImageListItem';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import type { AnalysisResultVariant } from '../../types';
import { ImageSkeletonList } from '../ImageSkeleton/ImageSkeleton';
import { NoData } from '../NoData/NoData';
import { PhotoEvidenceListItem } from '../PhotoEvidenceListItem/PhotoEvidenceListItem';
import { Typography } from '../Typography/Typography';

export type PhotoEvidenceItem = Pick<ImageListItemProps, 'cols' | 'rows'> & {
  alt?: string;
  id?: string | number;
  src: string;
  thumbnailSrc?: string;
  placeholderSrc?: string;
  isLoading?: boolean;
  uploadProgress?: number;
  analysisStatus?: AnalysisResultVariant;
  imageRejectionReasons?: string[];
  imageWarningReasons?: string[];
};

export type PhotoEvidencePlaceholderItem = Omit<PhotoEvidenceItem, 'id' | 'src'> & {
  id: string | number;
  src?: undefined;
};

export type PhotoEvidenceListPhoto = PhotoEvidenceItem | PhotoEvidencePlaceholderItem;

type Props = {
  openOnClick?: boolean;
  placeholderSrc?: string;
  evidencePhotos: PhotoEvidenceListPhoto[];
  emptyStateLabel?: ReactNode;
  direction?: 'row' | 'column';
  slotProps?: {
    imageList?: Omit<ImageListProps, 'children'>;
  };
  handleClick?: (photo: PhotoEvidenceItem) => void;
  isLoading?: boolean;
  obfuscateAi?: boolean;
  showValueCounter?: boolean;
  valueCounterSeparator?: ReactNode;
  imageLoadingStrategy?: 'progressive' | 'eager';
  getPhotoImageSx?: (photo: PhotoEvidenceListPhoto) => SxProps<Theme> | undefined;
  getPhotoKey?: (photo: PhotoEvidenceListPhoto, index: number) => string;
  getPhotoListItemSx?: (photo: PhotoEvidenceListPhoto) => SxProps<Theme> | undefined;
  renderPhotoFooter?: (photo: PhotoEvidenceListPhoto) => ReactNode;
  renderPhotoPlaceholder?: (photo: PhotoEvidenceListPhoto) => ReactNode;
};

export const PhotoEvidenceList = (props: Props) => {
  const {
    emptyStateLabel,
    evidencePhotos,
    slotProps,
    openOnClick,
    isLoading,
    obfuscateAi,
    showValueCounter,
    valueCounterSeparator = '/',
    imageLoadingStrategy = 'progressive',
    getPhotoImageSx,
    getPhotoKey,
    getPhotoListItemSx,
    renderPhotoFooter,
    renderPhotoPlaceholder,
  } = props;
  const { isMobile } = useBreakpoint();

  if (!evidencePhotos.length && !isLoading) {
    return emptyStateLabel ? <NoData>{emptyStateLabel}</NoData> : null;
  }

  return (
    <ImageList
      gap={isMobile ? 10 : 20}
      cols={isMobile ? 2 : 4}
      {...slotProps?.imageList}
      sx={{
        gridAutoFlow: props.direction === 'row' ? 'column' : 'row',
        my: 0,
        ...(props.direction === 'row' && {
          gridTemplateColumns: 'auto !important',
          gridTemplateRows: `repeat(${slotProps?.imageList?.cols ?? 2}, 1fr)`,
        }),
      }}
    >
      {isLoading ? (
        <ImageSkeletonList />
      ) : (
        evidencePhotos.map((photo, index) => (
          <PhotoEvidenceListItem
            key={getPhotoKey?.(photo, index) ?? photo.id ?? photo.src ?? index}
            photo={photo}
            placeholderSrc={props.placeholderSrc}
            openOnClick={openOnClick}
            onClick={props.handleClick}
            obfuscateAi={obfuscateAi}
            imageLoadingStrategy={imageLoadingStrategy}
            imageSx={getPhotoImageSx?.(photo)}
            listItemSx={getPhotoListItemSx?.(photo)}
            footer={renderPhotoFooter?.(photo)}
            placeholder={renderPhotoPlaceholder?.(photo)}
            valueCounter={
              showValueCounter ? (
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography component="span" variant="inherit" fontWeight="bold">
                    {index + 1}
                  </Typography>
                  &nbsp;
                  <Typography component="span" variant="inherit">
                    {valueCounterSeparator}
                  </Typography>
                  &nbsp;
                  <Typography component="span" variant="inherit" fontWeight="bold">
                    {evidencePhotos.length}
                  </Typography>
                </Typography>
              ) : undefined
            }
          />
        ))
      )}
    </ImageList>
  );
};
